import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import utcPlugin from "dayjs/plugin/utc";
import { Link as RouterLink } from "react-router";
import { buildManualReviewUrl } from "components/labellerAccuracy/utils";
import { useTenantTranslation } from "hooks/formatters";
import {
  DailySessionDetail,
  LabellerDetails,
} from "src/store/api/generatedApi";

dayjs.extend(durationPlugin);
dayjs.extend(utcPlugin);

const TIMESTAMP_FORMAT = "YYYY-MM-DD HH:mm";

const formatDuration = (startedAt: string, endedAt: string): string => {
  const seconds = dayjs(endedAt).diff(dayjs(startedAt), "second");
  const duration = dayjs.duration(seconds, "seconds");
  if (duration.asHours() >= 1) {
    return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
  }
  if (duration.asMinutes() >= 1) {
    return `${duration.minutes()}m`;
  }
  return `${seconds}s`;
};

export type SessionWithTenant = DailySessionDetail & { tenantName: string };

export const DailySessionPanel = ({
  sessions,
  labeller,
}: {
  sessions: readonly SessionWithTenant[];
  labeller: LabellerDetails | null;
}) => {
  const { t } = useTenantTranslation();

  if (sessions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: "background.default" }}>
      <TableContainer
        sx={{
          maxHeight: 220,
          "& .MuiTableCell-stickyHeader": {
            backgroundColor: "background.paper",
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("type")}</TableCell>
              <TableCell>{t("location")}</TableCell>
              <TableCell>{t("footageStart")}</TableCell>
              <TableCell>{t("footageEnd")}</TableCell>
              <TableCell>{t("duration")}</TableCell>
              <TableCell>{t("viewLabels")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow
                key={`${session.tenantName}-${session.session_type}-${session.session_id}-${session.location_name}-${session.footage_start_at}`}
              >
                <TableCell>{t(session.session_type)}</TableCell>
                <TableCell>{session.location_name}</TableCell>
                <TableCell>
                  {dayjs.utc(session.footage_start_at).format(TIMESTAMP_FORMAT)}
                </TableCell>
                <TableCell>
                  {dayjs.utc(session.footage_end_at).format(TIMESTAMP_FORMAT)}
                </TableCell>
                <TableCell>
                  {formatDuration(
                    session.footage_start_at,
                    session.footage_end_at
                  )}
                </TableCell>
                <TableCell>
                  {labeller ? (
                    <Link
                      component={RouterLink}
                      to={buildManualReviewUrl({
                        tenantName: session.tenantName,
                        location: session.location_name,
                        labellerEventStart: session.footage_start_at,
                      })}
                      target="_blank"
                      rel="noopener"
                    >
                      {t("viewLabels")}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
